import React, { Dispatch, SetStateAction } from 'react';

import { useRouter } from 'expo-router';
import { MenuObjectSkeleton } from 'features/menu/components/menu-object-skeleton';
import { ScrollView } from 'react-native';

import { IPickerAspect } from '@rbi-ctg/menu';
import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import { LayoutContainer } from 'components/layout/styled';
import { NutritionInformation } from 'components/product-detail-v2/nutrition-information';
import { ProductCta } from 'components/product-detail-v2/product-cta';
import { ProductPickerFlow } from 'components/product-detail-v2/product-detail-flow/product-picker-flow';
import { ProductInformation } from 'components/product-detail-v2/product-information';
import { ProductOverview } from 'components/product-detail-v2/product-overview';
import { ProductUpsell } from 'components/product-detail-v2/product-upsell';
import {
  ProductOverviewContainer,
  hiddenStyles,
  hiddenStylesDesktop,
} from 'components/product-detail-v2/styled';
import { transformProductWizardStepToEventAttr } from 'components/product-detail-v2/utils';
import { MenuObjectTypes } from 'enums/menu';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import {
  ProductWizardStep,
  UserSelectionModifierSlotMap,
  UserSelectionPickerAspect,
} from 'state/product-wizard-v2/types';
import { ProductWizardUtils } from 'state/product-wizard-v2/utils';
import { primitive } from 'styles/constants';
import { isCombo, isItem, isPicker } from 'utils/menu';

import { ProductComboFlow } from '../product-combo-flow';
import { ProductDetailHeader } from '../product-detail-header';
import { ProductItemFlow } from '../product-item-flow';
import { DEFAULT_SLOT_KEY } from '../product-item-flow/constants';
import { FIRST_INDEX } from '../product-picker-flow/constants';

import {
  BottomWrapper,
  ProductHomeInnerLayout,
  ProductHomeLayoutContainer,
  ProductSelectionsContainer,
} from './styled';

interface IProductDetailHomeProps {
  modifierSelections: UserSelectionModifierSlotMap;
  handleChangeModifierSelections: Dispatch<SetStateAction<UserSelectionModifierSlotMap>>;
  filteredPickerAspects: IPickerAspect[];
  getPreviousPickerSelectionIdentifiers: (startPoint: number, endPoint: number) => string[];
  selectedPickerAspects: UserSelectionPickerAspect;
  setSelectedPickerAspects: React.Dispatch<React.SetStateAction<UserSelectionPickerAspect>>;
}

export const ProductDetailHome = ({
  modifierSelections,
  handleChangeModifierSelections,
  filteredPickerAspects,
  getPreviousPickerSelectionIdentifiers,
  selectedPickerAspects,
  setSelectedPickerAspects,
}: IProductDetailHomeProps) => {
  const isMobile = useIsMobileBreakpoint();
  const { currentStep, slotKeyToCustomize, id } = useProductWizardFlow();
  const { isTakeoverActive } = useAppTakeoverPdp(id);
  const { back } = useRouter();

  const {
    menuObject,
    userSelections,
    updateProductWizardFlowSteps,
    selectedProduct,
  } = useProductWizardContext();

  const [isSkeletonVisible, setIsSkeletonVisible] = React.useState(false);

  const hasLessThanTwoPickerAspects = isPicker(menuObject) && menuObject.pickerAspects.length < 2;
  const isItemNotCustomizableOrHasNoOptions =
    isItem(selectedProduct) &&
    (!ProductWizardUtils.IsCustomizable(selectedProduct) || !selectedProduct.options.length);
  const selectedProductItemNotCustomizable =
    hasLessThanTwoPickerAspects && isItemNotCustomizableOrHasNoOptions;

  const shouldDisplayProductUpsell =
    isCombo(menuObject) || isItem(menuObject) || selectedProductItemNotCustomizable;

  React.useEffect(() => {
    const firstPickerAspectId = isPicker(menuObject) ? filteredPickerAspects[FIRST_INDEX]._id : '';

    if (firstPickerAspectId) {
      updateProductWizardFlowSteps({
        id: firstPickerAspectId,
        type: MenuObjectTypes.PICKER_ASPECT,
        currentStep: true,
        isFirstStep: true,
      });
    }
  }, [filteredPickerAspects, menuObject, updateProductWizardFlowSteps]);

  const renderByMenuObjectType = React.useCallback(() => {
    switch (menuObject._type) {
      case MenuObjectTypes.ITEM:
        const selectedItem = userSelections.comboSlot?.get(DEFAULT_SLOT_KEY)?.selectedItem || null;
        return (
          <ProductItemFlow
            selectedItem={selectedItem}
            modifierSelections={modifierSelections}
            setModifierSelections={handleChangeModifierSelections}
          />
        );
      case MenuObjectTypes.COMBO:
        return <ProductComboFlow />;
      case MenuObjectTypes.PICKER:
        const userSelectedPickerAspects = userSelections.pickerAspects;
        return (
          <ProductPickerFlow
            pickerData={menuObject}
            userSelectedPickerAspects={userSelectedPickerAspects}
            // Use the slotKeyToCustomize if it exists, otherwise fallback to use the first picker aspect
            pickerSlotNumberToCustomize={
              slotKeyToCustomize || menuObject.pickerAspects[FIRST_INDEX]._id
            }
            filteredPickerAspects={filteredPickerAspects}
            getPreviousPickerSelectionIdentifiers={getPreviousPickerSelectionIdentifiers}
            selectedPickerAspects={selectedPickerAspects}
            setSelectedPickerAspects={setSelectedPickerAspects}
          />
        );
      default:
        return null;
    }
  }, [
    filteredPickerAspects,
    getPreviousPickerSelectionIdentifiers,
    handleChangeModifierSelections,
    menuObject,
    modifierSelections,
    selectedPickerAspects,
    setSelectedPickerAspects,
    slotKeyToCustomize,
    userSelections.comboSlot,
    userSelections.pickerAspects,
  ]);

  const backgroundColor = isTakeoverActive ? undefined : primitive.$white;

  return (
    <>
      <ProductDetailHeader
        currentStep={currentStep}
        onPress={back}
        eventAttributes={{
          name: transformProductWizardStepToEventAttr(ProductWizardStep.ProductHome),
        }}
      />

      <ScrollView
        contentContainerStyle={
          isSkeletonVisible ? (isMobile ? hiddenStyles : hiddenStylesDesktop) : undefined
        }
        showsVerticalScrollIndicator
      >
        {isSkeletonVisible && (
          <LayoutContainer flexBasis="100%" isFullContainer={isMobile}>
            <MenuObjectSkeleton />
          </LayoutContainer>
        )}

        <ProductOverviewContainer backgroundColor={backgroundColor}>
          <ProductOverview
            onImageLoaded={() => {
              setIsSkeletonVisible(false);
            }}
          />
        </ProductOverviewContainer>

        <ProductSelectionsContainer
          testID="product-home-customization-panel"
          backgroundColor={backgroundColor}
        >
          <ProductHomeLayoutContainer isFullContainer={isMobile}>
            <ProductHomeInnerLayout $isFullContainer={isMobile}>
              {renderByMenuObjectType()}
            </ProductHomeInnerLayout>
          </ProductHomeLayoutContainer>
        </ProductSelectionsContainer>

        <ProductHomeLayoutContainer
          isFullContainer={isMobile}
          backgroundColor={!isTakeoverActive ? primitive.$mayoEggWhite : undefined}
        >
          <ProductHomeInnerLayout>
            <BottomWrapper>
              {shouldDisplayProductUpsell && <ProductUpsell />}
              <ProductInformation />
              <NutritionInformation />
            </BottomWrapper>
          </ProductHomeInnerLayout>
        </ProductHomeLayoutContainer>
      </ScrollView>

      <ProductCta
        currentStep={currentStep}
        menuObject={menuObject}
        modifierSelections={modifierSelections}
      />
    </>
  );
};
